import { MatDialog } from '@angular/material/dialog';
import { System } from '../../shared-components/System';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { CommunicationLogFilterParam } from './communication-log-param';
import { EventEmitter, Injectable } from '@angular/core';
import { CommunicationLog } from './models/communication-log.model';
import { map, take } from 'rxjs/operators';
import { Lookup } from '../../shared-components/lookup/lookup.model';
import { School } from '../../shared-components/year/school.model';
import { SharedService } from '../../shared-components/shared.service';
import { LoginService } from '../login/login.service';
import { Attachment } from '../../shared-components/models/attachment.model';
import { AddAttachmentDialogComponent } from '../../shared-components/add-attachment-dialog/add-attachment-dialog.component';

@Injectable({
    providedIn: 'root'
})
export class CommunicationLogService {

    addEditSchools: School[];
    viewSchools: School[];

    _filterParam: CommunicationLogFilterParam;
    filterChange = new EventEmitter<any>();

    constructor(
        private http: HttpClient,
        private sharedService: SharedService,
        private loginService: LoginService,
        private dialog: MatDialog
    ) {
        this._filterParam = new CommunicationLogFilterParam();
    }

    set filterParam(filterParam: CommunicationLogFilterParam) {
        this._filterParam = filterParam;
        this.filterChange.emit();
    }

    get filterParam() {
        return this._filterParam;
    }

    isFiltered(): boolean {
        return !!this.filterParam.studentNumber ||
            (
                this.filterParam.students != null &&
                this.filterParam.students.length > 0
            ) ||
            !!this.filterParam.type ||
            !!this.filterParam.subject ||
            !!this.filterParam.result ||
            !!this.filterParam.toLogDate ||
            !!this.filterParam.fromLogDate;
    }

    getStaffIdByUserId() {
        return this.loginService.getStaffIdByUserId();
    }

    openAddEditAttachmentDialog(
        communicationLog: CommunicationLog,
        attachment: Attachment,
        documentTypes: Lookup[],
        callBack: (saved: boolean, attachment: Attachment) => void
    ) {

        // Initializing attachment
        let newAttachment = false;
        if (!attachment) {
            newAttachment = true;
            attachment = new Attachment();
            attachment.Id = -(communicationLog.AttachmentRecords.length + 1);
            attachment.DocumentId = 0;
            attachment.CommunicationLogId = communicationLog.Id;
            attachment.FileName = '';
            attachment.Description = '';
            attachment.UploadedDate = new Date();
            attachment.MyFileBase64String = '';
            attachment.FileType = '';
            attachment.Comment = '';
            attachment.ViewFileOnWebsite = true;
        }

        // Opening the dialog
        const dialogBox = this.dialog.open<AddAttachmentDialogComponent>(AddAttachmentDialogComponent, {
            data: {
                attachment: attachment,
                newAttachment: newAttachment,
                editAttachment: !newAttachment,
                requireDocumentType: this.loginService.LoginSettings.V10,
                documentTypes: documentTypes
            },
            disableClose: true
        });
        dialogBox.afterClosed().pipe(take(1)).subscribe((saved: boolean) => {
            callBack(saved, attachment);
        });
    }

    getCommunicationLog(yearId: number, schoolId: number, studentId: number, staffId: number, LogId: number, singleLineNote: any, LogIds: string = null, includeMoreAreaData = true, cardLogs = false) {
        let paramString = '';
        paramString += '?yearId=' + yearId +
            '&schoolId=' + schoolId +
            '&studentId=' + studentId +
            '&staffId=' + staffId +
            '&LogId=' + LogId +
            '&singleLineNote=' + singleLineNote +
            '&LogIds=' + LogIds +
            '&includeMoreAreaData=' + includeMoreAreaData +
            '&cardLogs=' + cardLogs;
        return this.http.get<CommunicationLog[]>(System.apiURL.Classroom + 'GetCommunicationLog' + paramString).pipe(
            map(logs => {

                // Changing reason to an array
                for (let i = 0; i < logs.length; i++) {
                    logs[i] = System.convert(logs[i], new CommunicationLog());
                    const log = logs[i];
                    log.Date = new Date(log.Date);
                    log.Time = new Date(log.Time);
                    if (log.Reason) {
                        log.Reason = (log.Reason as string).split(',');

                        // Classic saves Reason with spaces after each comma
                        // These need to be trimmed to match the value in 360
                        for (let j = 0; j < log.Reason.length; j++) {
                            const reasonString: string = log.Reason[j];
                            log.Reason[j] = reasonString.trim();
                        }
                    }
                    if (log.InformOthers) {
                        log.InformOthers = (log.InformOthers as string).split(',');
                    }
                }

                // Returning the logs
                return logs;
            })
        );
    }

    getCommunicationLogCount(yearId: number, schoolId: number, studentId: number, staffId: number): Observable<number> {
        let paramString = '';
        paramString += '?yearId=' + yearId +
            '&schoolId=' + schoolId +
            '&studentId=' + studentId +
            '&staffId=' + staffId;
        return this.http.get<any>(System.apiURL.Classroom + 'GetCommunicationLogCount' + paramString);
    }

    convertReasons(log: CommunicationLog, reasons: Lookup[] = []) {
        let reasonsString = '';
        if (log.Reason && log.Reason instanceof Array) {
            log.Reason.forEach(reason => {
                if (reasonsString.length > 0) {
                    reasonsString += ',';
                }
                reasonsString += reason;
            });
            log.Reason = reasonsString;
        } else if (log.Reason == 'all' && reasons) {
            reasons.forEach(reason => {
                if (reasonsString.length > 0) {
                    reasonsString += ',';
                }
                reasonsString += reason.Code;
            });
            log.Reason = reasonsString;
        }
    }

    convertInformOthers(log: CommunicationLog, others: Lookup[] = []) {
        let othersString = '';
        if (log.InformOthers && log.InformOthers instanceof Array) {
            log.InformOthers.forEach(other => {
                if (othersString.length > 0) {
                    othersString += ',';
                }
                othersString += other;
            });
            log.InformOthers = othersString;
        } else if (log.InformOthers == 'all' && others) {
            others.forEach(other => {
                if (othersString.length > 0) {
                    othersString += ',';
                }
                othersString += other.Code;
            });
            log.InformOthers = othersString;
        }
    }

    saveCommunicationLog(yearId: number, schoolId: number, data: CommunicationLog, reasons: Lookup[] = null, informOthers: Lookup[] = null): Observable<boolean> {

        // Changing reason from array to CSV
        // NOTE: we copy to prevent other errors from propagating
        const copyLog = new CommunicationLog();
        Object.assign(copyLog, data);
        this.convertReasons(copyLog, reasons);
        this.convertInformOthers(copyLog, informOthers);

        let paramString = '';
        paramString += '?yearId=' + yearId +
            '&schoolId=' + schoolId;
        return this.http.post<boolean>(System.apiURL.Classroom + 'SaveCommunicationLog' + paramString, copyLog);
    }

    saveCommunicationLogs(yearId: number, schoolId: number, data: CommunicationLog[], reasons: Lookup[] = null, informOthers: Lookup[] = null): Observable<boolean> {

        // Changing reason from array to CSV
        // NOTE: we copy to prevent other errors from propagating
        const copyLogs = [];
        data.forEach(datum => {
            const copyLog = new CommunicationLog();
            Object.assign(copyLog, datum);
            this.convertReasons(copyLog, reasons);
            this.convertInformOthers(copyLog, informOthers);
            copyLogs.push(copyLog);
        });

        let paramString = '';
        paramString += '?yearId=' + yearId +
            '&schoolId=' + schoolId;
        return this.http.post<boolean>(System.apiURL.Classroom + 'SaveCommunicationLogs' + paramString, copyLogs);
    }

    deleteCommunicationLog(logId: number): Observable<any> {
        let paramString = '';
        paramString += '?logId=' + logId;
        return this.http.post<any>(System.apiURL.Classroom + 'DeleteCommunicationLog' + paramString, null);
    }

    saveCommunicationLogAttachment(AttachmentRecords: any): Observable<any> {
        return this.http.post<any>(System.apiURL.Classroom + 'SaveCommunicationLogAttachment', AttachmentRecords);
    }

    deleteCommunicationLogAttachment(logId: number): Observable<any> {
        let paramString = '';
        paramString += '?Id=' + logId;
        return this.http.post<any>(System.apiURL.Classroom + 'DeleteCommunicationLogAttachment' + paramString, null);
    }

    getCommunicationLogAttachment(attachmentId: number, massEmailAttachment: boolean): Observable<string> {
        return this.http.get<any>(System.apiURL.Classroom + 'GetCommunicationLogAttachment?attachmentId=' + attachmentId + '&massEmailAttachment=' + massEmailAttachment);
    }

    getAttachmentById(id: number): Observable<any> {
        return this.sharedService.getAttachment(id, '');
    }

}
